import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import { UserAvatar } from "@/components/UserAvatar";
import { useDebounce } from "@/hooks/useDebounce";
import { useToast } from "@/hooks/use-toast";
import { useTeams } from "@/hooks/useTeams";
import { useCreateUser, useDeleteUser, useUpdateUser, useUpdateUserRole, useUpdateUserStatus, useUsers } from "@/hooks/useUsers";
import { Department, SeniorityLevel, User, UserRole, UserStatus } from "@/types";
import { Check, ChevronsUpDown, Edit2, Loader2, Plus, Search, Shield, Trash2, User as UserIcon, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useState } from "react";
import { format } from "date-fns";
import { InlineTextInput } from "./inline-text-input";
import { JoinDateInput } from "./join-date-input";
import { ModernPagination } from "./ModernPagination";
import { DEPARTMENT_OPTIONS, SENIORITY_LEVEL_OPTIONS } from "./staff-profile-options";
import { StaffProfileFields } from "./StaffProfileFields";

/**
 * The users list renders the manager from the nested `manager` object, but the scalar
 * `managerPid` is not always present on the same payload. Reading only the scalar made
 * the Clear action and the selected-row check vanish on rows that visibly had a
 * manager — leaving a wrongly assigned manager with no way to remove it.
 */
function getManagerPid(user: User): string | undefined {
    return user.manager?.pid ?? user.managerPid;
}

export function UserManagement() {
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 10;

    const { data: usersResponse, isLoading } = useUsers(searchQuery, page, pageSize);
    const users = usersResponse?.data || [];
    const totalUsers = usersResponse?.total || 0;
    const totalPages = Math.ceil(totalUsers / pageSize);

    const { data: teamsResponse } = useTeams();
    const teams = teamsResponse?.data || [];

    const createUser = useCreateUser();
    const updateUser = useUpdateUser();
    const updateRole = useUpdateUserRole();
    const updateStatus = useUpdateUserStatus();
    const deleteUser = useDeleteUser();
    const { toast } = useToast();

    // Dialog state
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    // Form state
    const [formData, setFormData] = useState<Partial<User>>({
        fullName: "",
        email: "",
        position: "",
        teamPid: "",
    });

    // Manager selection state for inline editing
    const [openManagerDropdown, setOpenManagerDropdown] = useState<string | null>(null);
    const [managerSearchQuery, setManagerSearchQuery] = useState("");
    
    // Debounce the manager search query to reduce API calls
    const debouncedManagerSearch = useDebounce(managerSearchQuery, 300);
    
    // Fetch managers based on debounced search query (larger page size to get more results)
    const { data: managersResponse, isLoading: isLoadingManagers } = useUsers(
        debouncedManagerSearch, 
        1, 
        5 // Get up to 5 managers in search results
    );

    const resetForm = () => {
        setFormData({ fullName: "", email: "", position: "", teamPid: "" });
        setSelectedUser(null);
    };

    const handleAddClick = () => {
        resetForm();
        setIsFormOpen(true);
    };

    const handleEditClick = (user: User) => {
        setSelectedUser(user);
        setFormData({
            fullName: user.fullName,
            email: user.email,
            position: user.position || "",
            seniorityLevel: user.seniorityLevel,
            department: user.department,
            joinDate: user.joinDate ? new Date(user.joinDate) : undefined,
        });
        setIsFormOpen(true);
    };

    const handleDeleteClick = (user: User) => {
        setSelectedUser(user);
        setIsDeleteDialogOpen(true);
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedUser) {
            // joinDate must go over the wire as a date-only string (yyyy-MM-dd) — sending
            // the raw Date would serialize to a full ISO datetime and risk a timezone-shifted day.
            const updateData = {
                ...formData,
                ...(formData.joinDate ? { joinDate: format(formData.joinDate, "yyyy-MM-dd") as unknown as Date } : {}),
            };
            updateUser.mutate({ userPid: selectedUser.pid, data: updateData }, {
                onSuccess: () => {
                    toast({ title: "User updated", description: "User information has been successfully updated." });
                    setIsFormOpen(false);
                },
                onError: () => toast({ title: "Error", description: "Failed to update user.", variant: "destructive" })
            });
        } else {
            // Remove "none" value for teamPid if selected
            const submissionData = { ...formData };
            if (submissionData.teamPid === "none") {
                delete submissionData.teamPid;
            }

            createUser.mutate(submissionData, {
                onSuccess: () => {
                    toast({ title: "User created", description: "New staff member has been added." });
                    setIsFormOpen(false);
                },
                onError: () => toast({ title: "Error", description: "Failed to create user.", variant: "destructive" })
            });
        }
    };

    const confirmDelete = () => {
        if (selectedUser) {
            deleteUser.mutate(selectedUser.pid, {
                onSuccess: () => {
                    toast({ title: "User deleted", description: "Staff member has been removed." });
                    setIsDeleteDialogOpen(false);
                },
                onError: () => toast({ title: "Error", description: "Failed to delete user.", variant: "destructive" })
            });
        }
    };

    const handleRoleChange = (userPid: string, newRole: UserRole) => {
        updateRole.mutate({ userPid, roleName: newRole }, {
            onSuccess: () => {
                toast({
                    title: "Role updated",
                    description: "User role has been successfully updated.",
                });
            },
            onError: (error) => {
                toast({
                    title: "Error",
                    description: "Failed to update user role. Please try again.",
                    variant: "destructive",
                });
            }
        });
    };

    const handleStatusChange = (userPid: string, newStatus: UserStatus) => {
        updateStatus.mutate({ userPid, status: newStatus }, {
            onSuccess: () => {
                toast({
                    title: "Status updated",
                    description: "User status has been successfully updated.",
                });
            },
            onError: () => {
                toast({
                    title: "Error",
                    description: "Failed to update user status.",
                    variant: "destructive",
                });
            }
        });
    };

    const handleManagerChange = (userPid: string, newManagerPid: string | null) => {
        updateUser.mutate({ userPid, data: { managerPid: newManagerPid } }, {
            onSuccess: () => {
                toast({
                    title: "Manager updated",
                    description: "User's manager has been successfully updated.",
                });
                setOpenManagerDropdown(null);
                setManagerSearchQuery("");
            },
            onError: () => {
                toast({
                    title: "Error",
                    description: "Failed to update user's manager.",
                    variant: "destructive",
                });
            }
        });
    };

    const handleSeniorityLevelChange = (userPid: string, value: SeniorityLevel) => {
        updateUser.mutate({ userPid, data: { seniorityLevel: value } }, {
            onSuccess: () => {
                toast({ title: "Seniority level updated" });
            },
            onError: () => {
                toast({ title: "Error", description: "Failed to update seniority level.", variant: "destructive" });
            }
        });
    };

    const handleDepartmentChange = (userPid: string, value: Department) => {
        updateUser.mutate({ userPid, data: { department: value } }, {
            onSuccess: () => {
                toast({ title: "Department updated" });
            },
            onError: () => {
                toast({ title: "Error", description: "Failed to update department.", variant: "destructive" });
            }
        });
    };

    // updateUser is one shared mutation across every row, so isPending alone would
    // disable all rows during any single save — and blow away text being typed in
    // another row's join-date field. Scope the pending state to the row being saved.
    const isSavingRow = (userPid: string) => updateUser.isPending && updateUser.variables?.userPid === userPid;

    const handlePositionChange = (userPid: string, position: string) => {
        updateUser.mutate({ userPid, data: { position } }, {
            onSuccess: () => {
                toast({ title: "Position updated" });
            },
            onError: () => {
                toast({ title: "Error", description: "Failed to update position.", variant: "destructive" });
            }
        });
    };

    const handleJoinDateChange = (userPid: string, date: Date | undefined) => {
        if (!date) return;
        updateUser.mutate({ userPid, data: { joinDate: format(date, "yyyy-MM-dd") as unknown as Date } }, {
            onSuccess: () => {
                toast({ title: "Join date updated" });
            },
            onError: () => {
                toast({ title: "Error", description: "Failed to update join date.", variant: "destructive" });
            }
        });
    };

    // Pagination logic moved to ModernPagination

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-7">
                    <div>
                        <CardTitle>Staff Management</CardTitle>
                        <CardDescription>View all staff and manage their access levels</CardDescription>
                    </div>
                    <Button onClick={handleAddClick} className="gap-2">
                        <Plus className="h-4 w-4" />
                        Add Staff Member
                    </Button>
                </CardHeader>
                <CardContent>
                    <div className="mb-6 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search staff by name or email..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setPage(1);
                            }}
                            className="pl-9"
                        />
                    </div>

                    {isLoading ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Staff Member</TableHead>
                                    <TableHead>Position</TableHead>
                                    <TableHead>Seniority</TableHead>
                                    <TableHead>Department</TableHead>
                                    <TableHead>Join Date</TableHead>
                                    <TableHead>Manager</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {[1, 2, 3, 4].map((i) => (
                                    <TableRow key={i}>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <Skeleton className="h-9 w-9 rounded-full" />
                                                <div className="space-y-1.5">
                                                    <Skeleton className="h-4 w-28" />
                                                    <Skeleton className="h-3 w-36" />
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-6 w-16 rounded-full" /></TableCell>
                                        <TableCell><Skeleton className="h-6 w-16 rounded-full" /></TableCell>
                                        <TableCell className="text-right"><Skeleton className="h-8 w-16 ml-auto rounded-md" /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div className="space-y-4">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Staff Member</TableHead>
                                        <TableHead>Position</TableHead>
                                        <TableHead>Seniority</TableHead>
                                        <TableHead>Department</TableHead>
                                        <TableHead>Join Date</TableHead>
                                        <TableHead>Manager</TableHead>
                                        <TableHead>Role</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {users.length > 0 ? (
                                        users.map((user) => (
                                            <TableRow key={user.pid}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <UserAvatar
                                                            src={user.avatarUrl}
                                                            name={user.fullName}
                                                            className="h-9 w-9"
                                                        />
                                                        <div className="flex flex-col">
                                                            <span className="font-medium">{user.fullName}</span>
                                                            <span className="text-xs text-muted-foreground">{user.email}</span>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <InlineTextInput
                                                        className="w-40"
                                                        value={user.position}
                                                        placeholder="—"
                                                        onCommit={(value) => handlePositionChange(user.pid, value)}
                                                        disabled={isSavingRow(user.pid)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={user.seniorityLevel}
                                                        onValueChange={(value) => handleSeniorityLevelChange(user.pid, value as SeniorityLevel)}
                                                        disabled={updateUser.isPending}
                                                    >
                                                        <SelectTrigger className="w-32">
                                                            <SelectValue placeholder="—" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {SENIORITY_LEVEL_OPTIONS.map((option) => (
                                                                <SelectItem key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={user.department}
                                                        onValueChange={(value) => handleDepartmentChange(user.pid, value as Department)}
                                                        disabled={updateUser.isPending}
                                                    >
                                                        <SelectTrigger className="w-36">
                                                            <SelectValue placeholder="—" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {DEPARTMENT_OPTIONS.map((option) => (
                                                                <SelectItem key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <JoinDateInput
                                                        className="w-36"
                                                        value={user.joinDate ? new Date(user.joinDate) : undefined}
                                                        onChange={(date) => handleJoinDateChange(user.pid, date)}
                                                        disabled={isSavingRow(user.pid)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Popover 
                                                        open={openManagerDropdown === user.pid} 
                                                        onOpenChange={(open) => {
                                                            setOpenManagerDropdown(open ? user.pid : null);
                                                            if (!open) setManagerSearchQuery("");
                                                        }}
                                                    >
                                                        <PopoverTrigger asChild>
                                                            <Button
                                                                variant="outline"
                                                                role="combobox"
                                                                disabled={updateUser.isPending}
                                                                className="w-[200px] justify-between"
                                                            >
                                                                {user.manager ? (
                                                                    <div className="flex items-center gap-2 truncate">
                                                                        <UserAvatar
                                                                            src={user.manager.avatarUrl}
                                                                            name={user.manager.fullName}
                                                                            className="h-5 w-5"
                                                                        />
                                                                        <span className="truncate">{user.manager.fullName}</span>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted-foreground">No manager</span>
                                                                )}
                                                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-[300px] p-0" align="start">
                                                            <Command shouldFilter={false}>
                                                                <CommandInput 
                                                                    placeholder="Search managers..." 
                                                                    value={managerSearchQuery}
                                                                    onValueChange={setManagerSearchQuery}
                                                                />
                                                                <CommandList>
                                                                    {isLoadingManagers ? (
                                                                        <div className="flex items-center justify-center py-6">
                                                                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                                                        </div>
                                                                    ) : (
                                                                        <>
                                                                            <CommandEmpty>No manager found.</CommandEmpty>
                                                                            <CommandGroup>
                                                                                {getManagerPid(user) && (
                                                                                    <CommandItem
                                                                                        value="clear-manager"
                                                                                        onSelect={() => handleManagerChange(user.pid, null)}
                                                                                        className="text-muted-foreground"
                                                                                    >
                                                                                        <X className="mr-2 h-4 w-4" />
                                                                                        Clear manager
                                                                                    </CommandItem>
                                                                                )}
                                                                                {(managersResponse?.data || [])
                                                                                    .filter((potentialManager) => {
                                                                                        // Exclude self
                                                                                        return potentialManager.pid !== user.pid;
                                                                                    })
                                                                                    .map((potentialManager) => (
                                                                                        <CommandItem
                                                                                            key={potentialManager.pid}
                                                                                            value={potentialManager.pid}
                                                                                            onSelect={() => handleManagerChange(user.pid, potentialManager.pid)}
                                                                                        >
                                                                                            <Check
                                                                                                className={`mr-2 h-4 w-4 ${
                                                                                                    getManagerPid(user) === potentialManager.pid
                                                                                                        ? "opacity-100"
                                                                                                        : "opacity-0"
                                                                                                }`}
                                                                                            />
                                                                                            <UserAvatar
                                                                                                src={potentialManager.avatarUrl}
                                                                                                name={potentialManager.fullName}
                                                                                                className="h-6 w-6 mr-2"
                                                                                            />
                                                                                            <div className="flex flex-col">
                                                                                                <span className="font-medium">{potentialManager.fullName}</span>
                                                                                                <span className="text-xs text-muted-foreground">
                                                                                                    {potentialManager.position || potentialManager.email}
                                                                                                </span>
                                                                                            </div>
                                                                                        </CommandItem>
                                                                                    ))}
                                                                            </CommandGroup>
                                                                        </>
                                                                    )}
                                                                </CommandList>
                                                            </Command>
                                                        </PopoverContent>
                                                    </Popover>
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={user.role?.name || "user"}
                                                        onValueChange={(value) => handleRoleChange(user.pid, value as UserRole)}
                                                        disabled={updateRole.isPending}
                                                    >
                                                        <SelectTrigger className="w-32">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="user">
                                                                <div className="flex items-center gap-2">
                                                                    <UserIcon className="h-3.5 w-3.5" />
                                                                    <span>User</span>
                                                                </div>
                                                            </SelectItem>
                                                            <SelectItem value="admin">
                                                                <div className="flex items-center gap-2">
                                                                    <Shield className="h-3.5 w-3.5 text-primary" />
                                                                    <span>Admin</span>
                                                                </div>
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        value={user.status || "active"}
                                                        onValueChange={(value) => handleStatusChange(user.pid, value as UserStatus)}
                                                        disabled={updateStatus.isPending}
                                                    >
                                                        <SelectTrigger className="w-[110px]">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="active">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="h-2 w-2 rounded-full bg-green-500" />
                                                                    <span>Active</span>
                                                                </div>
                                                            </SelectItem>
                                                            <SelectItem value="inactive">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="h-2 w-2 rounded-full bg-gray-400" />
                                                                    <span>Inactive</span>
                                                                </div>
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleEditClick(user)}
                                                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                                        >
                                                            <Edit2 className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleDeleteClick(user)}
                                                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={9} className="h-24 text-center">
                                                No staff members found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>

                            <ModernPagination
                                page={page}
                                totalPages={totalPages}
                                onPageChange={setPage}
                                total={totalUsers}
                                limit={pageSize}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{selectedUser ? "Edit Staff Member" : "Add Staff Member"}</DialogTitle>
                        <DialogDescription>
                            {selectedUser ? "Update the details for this staff member." : "Fill in the information to add a new staff member to the organization."}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleFormSubmit} className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="fullName">Full Name</Label>
                            <Input
                                id="fullName"
                                required
                                value={formData.fullName}
                                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                                placeholder="John Doe"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">Email Address</Label>
                            <Input
                                id="email"
                                type="email"
                                required
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="john@example.com"
                                disabled={!!selectedUser}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="position">Position</Label>
                            <Input
                                id="position"
                                value={formData.position}
                                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                                placeholder="Software Engineer"
                            />
                        </div>
                        {selectedUser && (
                            <StaffProfileFields
                                seniorityLevel={formData.seniorityLevel}
                                department={formData.department}
                                joinDate={formData.joinDate}
                                onSeniorityLevelChange={(value: SeniorityLevel) => setFormData({ ...formData, seniorityLevel: value })}
                                onDepartmentChange={(value: Department) => setFormData({ ...formData, department: value })}
                                onJoinDateChange={(value) => setFormData({ ...formData, joinDate: value })}
                            />
                        )}
                        {!selectedUser && (
                            <div className="space-y-2">
                                <Label htmlFor="team">Assign to Team</Label>
                                <Select
                                    value={formData.teamPid}
                                    onValueChange={(value) => setFormData({ ...formData, teamPid: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a team (optional)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No Team</SelectItem>
                                        {teams.map((team) => (
                                            <SelectItem key={team.pid} value={team.pid}>
                                                {team.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <DialogFooter className="pt-4">
                            <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={createUser.isPending || updateUser.isPending}>
                                {(createUser.isPending || updateUser.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {selectedUser ? "Save Changes" : "Create Member"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently remove{" "}
                            <span className="font-semibold">{selectedUser?.fullName}</span> from the
                            organization and revoke their access.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteUser.isPending}
                        >
                            {deleteUser.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Delete Member
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
